import { useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Send, CheckCircle, Home, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BookOrderPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    shipping_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    quantity: 1,
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.customer_name || !form.customer_email || !form.shipping_address || !form.city || !form.state || !form.zip_code) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/book-orders`, form);
      setSubmitted(true);
      toast.success('Order submitted! Grad will contact you soon.');
    } catch (error) {
      toast.error('Failed to submit order. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-24 md:py-32" data-testid="book-order-success">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center"
          >
            <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Order Received!
            </h1>
            <p className="text-muted-foreground mb-8">
              Thank you for your order! Grad will review your request and contact you at <strong>{form.customer_email}</strong> with payment and shipping details.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/">
                <Button variant="outline" className="gap-2">
                  <Home className="w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/shop">
                <Button className="btn-primary gap-2">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32" data-testid="book-order-page">
      <div className="container-custom">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-10 h-10 text-accent" />
          </div>
          <h1 className="section-header mb-4">Order Your Copy</h1>
          <p className="section-subheader max-w-2xl mx-auto">
            Get "Oumaumau and the Star Sung Compass" directly from the author!
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          
          {/* Book Preview */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-card border-accent/30 overflow-hidden sticky top-24">
              <div className="aspect-[3/4] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/ppx9y8eq_cover%20main.jpg"
                  alt="Oumaumau and the Star Sung Compass"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  Oumaumau and the Star Sung Compass
                </h2>
                <p className="text-muted-foreground mb-4">
                  By Grad P Fawkes
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-accent">$17.99</span>
                  <span className="text-sm text-muted-foreground">+ Shipping</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Order Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-card border-border/50">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-6">Shipping Information</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={form.customer_name}
                      onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.customer_email}
                      onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      value={form.shipping_address}
                      onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                      placeholder="123 Main St, Apt 4"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        placeholder="City"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        value={form.state}
                        onChange={(e) => setForm({ ...form, state: e.target.value })}
                        placeholder="State"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="zip">ZIP Code *</Label>
                      <Input
                        id="zip"
                        value={form.zip_code}
                        onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                        placeholder="12345"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={form.country}
                        onChange={(e) => setForm({ ...form, country: e.target.value })}
                        placeholder="USA"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="quantity">Quantity</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 1 })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes">Special Instructions (Optional)</Label>
                    <Textarea
                      id="notes"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Any special requests or notes..."
                      rows={3}
                    />
                  </div>

                  <div className="pt-4 border-t border-border">
                    <div className="flex justify-between mb-4">
                      <span>Subtotal ({form.quantity} book{form.quantity > 1 ? 's' : ''})</span>
                      <span className="font-bold">${(17.99 * form.quantity).toFixed(2)}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Shipping costs will be calculated and sent to you via email.
                    </p>
                    <Button 
                      type="submit" 
                      className="w-full btn-primary h-12 text-lg"
                      disabled={submitting}
                    >
                      {submitting ? (
                        'Submitting...'
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Submit Order Request
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <p className="text-center text-sm text-muted-foreground mt-4">
              Grad will contact you directly to complete payment and confirm shipping details.
            </p>
          </motion.div>
        </div>

        {/* Navigation Links */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4 mt-16"
        >
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <Link to="/donate">
            <Button className="btn-primary gap-2">
              <Heart className="w-4 h-4" />
              Support / Donate
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
